import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, ChevronRight, Target, FolderOpen, CheckSquare, Check } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { motion } from "framer-motion";

export default function Dashboard() {
  const [goals, setGoals] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [hideCompleted, setHideCompleted] = useState(true);
  const navigate = useNavigate();
  const confirm = useConfirm();

  const fetchGoals = async () => {
    try { const r = await API.get("/goals/with-stats"); setGoals(r.data); }
    catch { try { const r = await API.get("/goals"); setGoals(r.data); } catch {} }
  };
  useEffect(() => { fetchGoals(); }, []);

  const toggleGoalComplete = async (goalId, current) => {
    if (current !== "completed") {
      if (!(await confirm("Mark Goal Complete", "All projects and tasks under this goal will also be marked as complete."))) return;
    }
    await API.patch("/goals/" + goalId, { status: current === "completed" ? "active" : "completed" });
    fetchGoals();
  };

  const handleCreate = async () => {
    if (!title.trim()) return;
    try { await API.post("/goals", { title, description }); toast.success("Goal created"); setTitle(""); setDescription(""); setShowCreate(false); fetchGoals(); }
    catch { toast.error("Failed"); }
  };

  const filteredGoals = hideCompleted ? goals.filter(g => g.status !== "completed") : goals;

  return (
    <div data-testid="dashboard-page" className="max-w-[900px]">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-[28px] font-bold text-[#37352F]">Goals</h1>
        <button onClick={() => setHideCompleted(!hideCompleted)} className={"text-[12px] px-2 py-1 border rounded-[4px] mr-2 transition-colors " + (hideCompleted ? "border-[#2383E2] text-[#2383E2] bg-[#2383E2]/5" : "border-[#E0E0DE] text-[#6B6B6B]")}>{hideCompleted ? "Show completed" : "Hide completed"}</button>
        <Button onClick={() => setShowCreate(true)} size="sm" className="bg-[#2383E2] hover:bg-[#1B6EC2] text-white text-[13px] h-8" data-testid="create-goal-button">
          <Plus className="w-3.5 h-3.5 mr-1" /> New Goal
        </Button>
      </div>
      <p className="text-[13px] text-[#6B6B6B] mb-6">Your aspirations, organized as chapters</p>

      {filteredGoals.length === 0 ? (
        <div className="text-center py-20">
          <Target className="w-8 h-8 text-[#B8B6B0] mx-auto mb-3" strokeWidth={1} />
          <p className="text-[#6B6B6B] text-[14px]">No goals yet. Begin your first chapter.</p>
        </div>
      ) : (
        <div className="space-y-px">
          {filteredGoals.map((goal, i) => {
            const taskCount = goal.task_count || 0;
            const doneCount = goal.done_count || 0;
            const projectCount = goal.project_count || 0;
            const progress = taskCount > 0 ? Math.round((doneCount / taskCount) * 100) : 0;
            return (
              <motion.div key={goal.goal_id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.04 }}
                onClick={() => navigate("/goals/" + goal.goal_id)}
                className="group flex items-center gap-4 py-3 px-3 -mx-3 rounded-[4px] hover:bg-[#F7F6F3] cursor-pointer transition-colors"
                data-testid={"goal-item-" + goal.goal_id}>
                <div onClick={e => e.stopPropagation()} className="shrink-0"><Checkbox checked={goal.status === "completed"} onCheckedChange={() => toggleGoalComplete(goal.goal_id, goal.status)} className="h-[18px] w-[18px] rounded-[3px] border-[#B8B6B0] data-[state=checked]:bg-[#2383E2] data-[state=checked]:border-[#2383E2] cursor-pointer" /></div>
                <div className="flex-1 min-w-0">
                  <h3 className={"text-[15px] font-medium truncate " + (goal.status === "completed" ? "line-through text-[#A3A3A0]" : "text-[#37352F]")}>{goal.title}</h3>
                  {goal.description && <p className="text-[13px] text-[#6B6B6B] truncate mt-0.5">{goal.description}</p>}
                  <div className="flex items-center gap-3 mt-1.5 text-[12px] text-[#6B6B6B]">
                    <span className="flex items-center gap-1"><FolderOpen className="w-3 h-3" />{projectCount}</span>
                    <span className="flex items-center gap-1"><CheckSquare className="w-3 h-3" />{doneCount}/{taskCount}</span>
                    {taskCount > 0 && (
                      <div className="flex items-center gap-1.5">
                        <div className="w-16 h-[3px] bg-[#E0E0DE] rounded-full"><div className="h-full bg-[#2383E2] rounded-full" style={{ width: progress + "%" }} /></div>
                        <span className="text-[11px]">{progress}%</span>
                      </div>
                    )}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-[#B8B6B0] group-hover:text-[#6B6B6B] transition-colors shrink-0" />
              </motion.div>
            );
          })}
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-white border-[#E0E0DE] shadow-sm">
          <DialogHeader><DialogTitle className="text-[15px] font-semibold text-[#37352F]">New Goal</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-[12px] text-[#6B6B6B]">Title</Label><Input value={title} onChange={e => setTitle(e.target.value)} className="mt-1 bg-transparent border-[#E0E0DE] text-[14px]" data-testid="goal-title-input" /></div>
            <div><Label className="text-[12px] text-[#6B6B6B]">Description</Label><Textarea value={description} onChange={e => setDescription(e.target.value)} className="mt-1 bg-transparent border-[#E0E0DE] resize-none text-[14px]" data-testid="goal-description-input" /></div>
          </div>
          <DialogFooter><Button onClick={handleCreate} className="bg-[#2383E2] hover:bg-[#1B6EC2] text-white text-[13px] h-8" data-testid="submit-goal-button">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
